import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../otherImages/MasterLayout";
import Swal from "sweetalert2";

const ManageServices = () => {
  const BASE_URL = "https://clearshine.koderspedia.online";

  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteService, setDeleteService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    key: "",
    value: "",
  });

  const normalizeServiceKey = (key) => {
    return String(key || "").trim().toLowerCase();
  };

  const showSuccess = (message) => {
    Swal.fire({
      icon: "success",
      title: "Success!",
      text: message,
      confirmButtonColor: "#0CAFE6",
    });
  };

  const showError = (message) => {
    Swal.fire({
      icon: "error",
      title: "Error!",
      text: message,
      confirmButtonColor: "#0CAFE6",
    });
  };

  const showLoading = (title = "Please wait...") => {
    Swal.fire({
      title,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  const getToken = () => localStorage.getItem("token");

  const getHeaders = () => {
    const token = getToken();

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const getServices = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/get-all-data`, {
        method: "GET",
        headers: getHeaders(),
      });

      const result = await response.json();

      if (response.ok && result?.success) {
        setServices(result?.data || []);
        setCurrentPage(1);
      } else {
        setServices([]);
        showError(result?.message || "Failed to fetch services");
      }
    } catch (error) {
      console.error("Get Services Error:", error);
      setServices([]);
      showError("Something went wrong while fetching services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getServices();
  }, []);

  const filteredServices = useMemo(() => {
    return services.filter((service) =>
      service.key?.toLowerCase().includes(search.toLowerCase())
    );
  }, [services, search]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.ceil(filteredServices.length / rowsPerPage) || 1;

  const paginatedServices = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return filteredServices.slice(startIndex, endIndex);
  }, [filteredServices, currentPage, rowsPerPage]);

  const paginationStart =
    filteredServices.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;

  const paginationEnd = Math.min(
    currentPage * rowsPerPage,
    filteredServices.length
  );

  const openCreateModal = () => {
    setEditingService(null);
    setFormData({
      key: "",
      value: "",
    });
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      key: service.key || "",
      value: service.value || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingService(null);
    setFormData({
      key: "",
      value: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "key" ? value.toLowerCase() : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!normalizeServiceKey(formData.key) || !formData.value) {
      showError("Key and value are required");
      return;
    }

    if (editingService) {
      await updateService();
    } else {
      await createService();
    }
  };

  const createService = async () => {
    try {
      setActionLoading(true);

      const response = await fetch(`${BASE_URL}/add-data`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          key: normalizeServiceKey(formData.key),
          value: formData.value,
        }),
      });

      const text = await response.text();
      console.log("Create Service Raw Response:", text);

      let result = {};

      if (text) {
        try {
          result = JSON.parse(text);
        } catch (error) {
          console.error("Create response is not JSON:", text);
          throw new Error("Server returned invalid response");
        }
      }

      console.log("Create Service Parsed Response:", result);

      if (response.ok && result?.success) {
        closeModal();
        await getServices();

        showSuccess(result?.message || "Service created successfully");
      } else {
        showError(
          result?.message || result?.error || "Failed to create service"
        );
      }
    } catch (error) {
      console.error("Create Service Error:", error);
      showError(error.message || "Something went wrong while creating service");
    } finally {
      setActionLoading(false);
    }
  };

  const updateService = async () => {
    try {
      setActionLoading(true);

      const response = await fetch(`${BASE_URL}/update-data`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          key: normalizeServiceKey(formData.key),
          value: formData.value,
        }),
      });

      const text = await response.text();
      console.log("Update Service Raw Response:", text);

      let result = {};

      if (text) {
        try {
          result = JSON.parse(text);
        } catch (error) {
          console.error("Update response is not JSON:", text);
          throw new Error("Server returned invalid response");
        }
      }

      console.log("Update Service Parsed Response:", result);

      if (response.ok && result?.success) {
        closeModal();
        await getServices();

        showSuccess(result?.message || "Service updated successfully");
      } else {
        showError(
          result?.message || result?.error || "Failed to update service"
        );
      }
    } catch (error) {
      console.error("Update Service Error:", error);
      showError(error.message || "Something went wrong while updating service");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteService?.id) {
      showError("Service ID not found");
      return;
    }

    try {
      setActionLoading(true);
      showLoading("Deleting service...");

      const response = await fetch(`${BASE_URL}/delete-data/${deleteService.id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      const text = await response.text();
      console.log("Delete Service Raw Response:", text);

      let result = {};

      if (text) {
        try {
          result = JSON.parse(text);
        } catch (error) {
          console.error("Delete response is not JSON:", text);
          throw new Error("Server returned invalid response");
        }
      }

      Swal.close();

      console.log("Delete Service Parsed Response:", result);

      if (response.ok && result?.success !== false) {
        setDeleteService(null);
        await getServices();

        showSuccess(result?.message || "Service deleted successfully");
      } else {
        showError(
          result?.message || result?.error || "Failed to delete service"
        );
      }
    } catch (error) {
      console.error("Delete Service Error:", error);
      Swal.close();
      showError(error.message || "Something went wrong while deleting service");
    } finally {
      setActionLoading(false);
    }
  };

  const activeServicesCount = services.length;

  const avgPrice =
    services.length > 0
      ? (
        services.reduce((sum, item) => sum + Number(item.value || 0), 0) /
        services.length
      ).toFixed(0)
      : 0;

  return (
    <MasterLayout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h4 className="fw-bold mb-8">Services</h4>
          <p className="text-secondary-light mb-0">
            Create, update, delete, and see booking pricing.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-dark d-flex align-items-center gap-2"
          onClick={openCreateModal}
        >
          <Icon icon="ic:round-plus" width="22" height="22" />
          Add New Service
        </button>
      </div>

      <div className="row gy-4 mb-24">
        <div className="col-xxl-3 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-secondary-light mb-8">Total Services</p>
                <h4 className="mb-0">{services.length}</h4>
              </div>
              <div className="w-48-px h-48-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center">
                <Icon icon="carbon:dns-services" width="22" height="22" />
              </div>
            </div>
          </div>
        </div>

        <div className="col-xxl-3 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-secondary-light mb-8">Active Services</p>
                <h4 className="mb-0">{activeServicesCount}</h4>
              </div>
              <div className="w-48-px h-48-px rounded-circle bg-success-focus d-flex justify-content-center align-items-center">
                <Icon icon="mdi:check-circle-outline" width="26" height="26" />
              </div>
            </div>
          </div>
        </div>

        <div className="col-xxl-3 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-secondary-light mb-8">Avg Price</p>
                <h4 className="mb-0">${avgPrice}</h4>
              </div>
              <div className="w-48-px h-48-px rounded-circle bg-info-focus d-flex justify-content-center align-items-center">
                <Icon icon="solar:dollar-outline" width="26" height="26" />
              </div>
            </div>
          </div>
        </div>

        <div className="col-xxl-3 col-sm-6">
          <div className="card p-20 radius-12 border-0 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-secondary-light mb-8">Last Service</p>
                <h4 className="mb-0 text-capitalize">
                  {services[0]?.key || "-"}
                </h4>
              </div>
              <div className="w-48-px h-48-px rounded-circle bg-warning-focus d-flex justify-content-center align-items-center">
                <Icon
                  icon="mdi:clipboard-text-clock-outline"
                  width="26"
                  height="26"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card basic-data-table">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <h5 className="card-title mb-0">Service List</h5>

          <div className="d-flex align-items-center gap-2">
            <div className="navbar-search">
              <input
                type="text"
                name="search"
                placeholder="Search service"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={getServices}
            >
              <Icon icon="mdi:refresh" width="20" height="20" />
              Refresh
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Service Name</th>
                  <th>Rate</th>
                  <th className="text-center">Manage</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-24">
                      Loading services...
                    </td>
                  </tr>
                ) : filteredServices.length > 0 ? (
                  paginatedServices.map((service) => (
                    <tr key={service.id}>
                      <td>{service.id}</td>

                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div className="w-40-px h-40-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center">
                            <Icon
                              icon="carbon:dns-services"
                              width="22"
                              height="22"
                            />
                          </div>

                          <div>
                            <h6 className="text-md mb-0 text-capitalize">
                              {service.key}
                            </h6>
                            <span className="text-sm text-secondary-light">
                              Service Key: {service.key}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="fw-bold">${service.value} / sq ft</span>
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            type="button"
                            className="w-36-px h-36-px bg-primary-50 text-primary-600 rounded-circle d-flex justify-content-center align-items-center border-0"
                            onClick={() => openEditModal(service)}
                            title="Edit"
                          >
                            <Icon icon="line-md:edit" width="20" height="20" />
                          </button>

                          <button
                            type="button"
                            className="w-36-px h-36-px bg-danger-focus text-danger-main rounded-circle d-flex justify-content-center align-items-center border-0"
                            onClick={() => setDeleteService(service)}
                            title="Delete"
                          >
                            <Icon
                              icon="material-symbols:delete-outline"
                              width="20"
                              height="20"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-24">
                      No services found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredServices.length > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 mt-24">
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary-light">Rows per page:</span>

                <select
                  className="form-select"
                  style={{ width: "80px" }}
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <span className="text-secondary-light">
                {paginationStart}-{paginationEnd} of {filteredServices.length}
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-3"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  title="Previous Page"
                >
                  <Icon icon="mdi:chevron-left" width="22" />
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary px-3"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  title="Next Page"
                >
                  <Icon icon="mdi:chevron-right" width="22" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.45)",
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-12">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingService ? "Update Service" : "Create Service"}
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row gy-3">
                    <div className="col-12">
                      <label className="form-label">Service Key</label>
                      <input
                        type="text"
                        name="key"
                        className="form-control"
                        placeholder="Example: cleaning"
                        value={formData.key}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Service Rate ($ per sq/ft)</label>
                      <input
                        type="number"
                        name="value"
                        className="form-control"
                        placeholder="Example: 30"
                        value={formData.value}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        required
                      />

                      {/* <small className="text-secondary-light d-block mt-2">
                        This rate is used as: total price = measured sq/ft × service rate.
                      </small> */}
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeModal}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={actionLoading}
                  >
                    {actionLoading
                      ? "Please wait..."
                      : editingService
                        ? "Update Service"
                        : "Create Service"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {deleteService && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.45)",
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-12">
              <div className="modal-header">
                <h5 className="modal-title">Delete Service</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteService(null)}
                ></button>
              </div>

              <div className="modal-body">
                <p className="mb-0">
                  Are you sure you want to delete{" "}
                  <strong>{deleteService.key}</strong>?
                </p>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setDeleteService(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default ManageServices;